import React, { useContext } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Typography, Alert } from 'antd'
import { PiChatCircleTextBold } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { ThemeContext } from '../../../../context/themeContext'
import { IFeedbackInfo } from '../../types'
import { getEnergyModeConfig } from '../../services/energy-mode-config'

dayjs.extend(relativeTime)

const { Text } = Typography

interface FeedbackInfoProps {
  feedbackInfo: IFeedbackInfo
}

const FeedbackInfo: React.FC<FeedbackInfoProps> = ({ feedbackInfo }) => {
  const { formatMessage } = useIntl()
  const { theme } = useContext(ThemeContext)
  const isDarkTheme = theme === DARK_THEME

  const { weight, feedbackScore, latestFeedbackTimestamp } = feedbackInfo

  // If no weight, feedback is too old - don't show anything
  if (weight === 0) {
    return null
  }

  // Determine message based on feedback score
  const getFeedbackMessage = () => {
    const weightPercent = Math.round(weight * 100)
    const when = dayjs(latestFeedbackTimestamp).fromNow()
    const energyModeConfig = getEnergyModeConfig(feedbackScore, formatMessage)

    return (<IntlMessages id="energyLevel.feedbackInfo" values={{
      energyLevel: energyModeConfig.name,
      weight: weightPercent,
      when,
      weightRemaining: 100 - weightPercent,
      energyLevelColor: (...chunks) => (
        <Text
          strong
          className="energy-level-color-text"
          style={{
            ['--energy-level-color' as string]: energyModeConfig.primaryColor,
          }}
        >
          {chunks}
        </Text>
      ),
      b: (...chunks) => (
        <Text strong>{chunks}</Text>
      ),
    }} />)
  }

  return (
    <Alert
      message={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PiChatCircleTextBold style={{ fontSize: '16px' }} />
          <Text strong>
            {formatMessage({ id: 'energyLevel.yourFeedbackTitle' })}
          </Text>
        </div>
      }
      description={getFeedbackMessage()}
      type="info"
      showIcon={false}
      style={{
        marginTop: 16,
        backgroundColor: isDarkTheme ? '#374151' : '#f3f4f6',
        border: 'none',
        color: isDarkTheme ? '#9ca3af' : '#6b7280',
      }}
    />
  )
}

export default FeedbackInfo

